import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      content: "Hey - I'm the UVD Media assistant. Ask me about our cinematic book trailers, pricing, or process. How can I help?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });

      if (response.data.success) {
        setMessages(prev => [...prev, { role: 'bot', content: response.data.response }]);
        if (!sessionId) setSessionId(response.data.session_id);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: "I'm having trouble responding right now. Please try again or contact us at info@uniquevisiondrones.com." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickQuestions = [
    "What are your prices?",
    "How long does a trailer take?",
    "What do I need to get started?"
  ];

  return (
    <>
      {/* Chat Button - Dark red, sharp */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 bg-red-800 hover:bg-red-700 text-white p-4 shadow-2xl shadow-red-900/30 transition-all duration-300 ${isOpen ? 'scale-0' : 'scale-100'}`}
        aria-label="Open chat"
        data-testid="chatbot-toggle"
      >
        <MessageCircle size={24} />
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-[#0A0A0A] shadow-2xl border border-white/10 transition-all duration-300 transform ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}
        style={{ maxHeight: 'calc(100vh - 100px)' }}
        data-testid="chatbot-window"
      >
        {/* Header */}
        <div className="bg-red-900 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/10 flex items-center justify-center">
              <Bot size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">UVD Media Assistant</h3>
              <p className="text-red-300 text-[10px] uppercase tracking-wider">Ask me anything</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white/60 hover:text-white transition-colors"
            aria-label="Close chat"
            data-testid="chatbot-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Messages */}
        <div className="h-[350px] overflow-y-auto p-4 space-y-4">
          {messages.map((msg, index) => (
            <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-red-800 text-white'
                  : 'bg-white/5 text-gray-200 border border-white/10'
              }`}>
                <div className="flex items-start gap-2">
                  {msg.role === 'bot' && <Bot size={14} className="text-red-500 mt-0.5 flex-shrink-0" />}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === 'user' && <User size={14} className="text-red-300 mt-0.5 flex-shrink-0" />}
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white/5 border border-white/10 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Bot size={14} className="text-red-500" />
                  <div className="flex gap-1">
                    {[0, 150, 300].map((delay) => (
                      <span key={delay} className="w-1.5 h-1.5 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Questions */}
        {messages.length === 1 && (
          <div className="px-4 pb-2">
            <p className="text-gray-600 text-[10px] uppercase tracking-wider mb-2">Quick questions:</p>
            <div className="flex flex-wrap gap-2">
              {quickQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => setInputValue(q)}
                  className="text-xs bg-white/5 hover:bg-red-900/30 text-gray-400 hover:text-white px-3 py-1.5 border border-white/10 hover:border-red-700/30 transition-all duration-200"
                  data-testid={`quick-question-${i}`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 bg-transparent border border-white/10 px-4 py-2 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-red-600 transition-colors"
              disabled={isLoading}
              data-testid="chatbot-input"
            />
            <button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              className="bg-red-700 hover:bg-red-800 disabled:opacity-40 text-white p-2 h-10 w-10 flex items-center justify-center transition-colors"
              data-testid="chatbot-send"
            >
              <Send size={16} />
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Chatbot;
